import styled from "@emotion/styled";
import {
  Caption,
  ParagraphVariantSizes,
  Spacer,
  Spacings,
  TableBody,
  TableHead,
  TableRow,
} from "@polestar/component-warehouse-react";
import type { HTMLElementMap } from "@polestar/component-warehouse-react/components/convert-htmlto-components/ConvertHTMLtoComponents";
import React, { type HTMLAttributes, type ReactElement } from "react";

import {
  ContentHeading,
  ContentOrderedList,
  ContentParagraph,
  ContentUnorderedList,
} from "~/common/components";

/**
 * The semantic structure of the table body depends on if a header is present.
 *
 * If it is, the first row will only have one child (<th />).
 */
type TableBodyWithHeader = [
  ReactElement<
    HTMLAttributes<HTMLTableRowElement> & {
      children: [ReactElement<HTMLAttributes<HTMLTableCellElement>>];
    }
  >,
  ...Array<
    ReactElement<
      HTMLAttributes<HTMLTableRowElement> & {
        children: [
          ReactElement<HTMLAttributes<HTMLTableCellElement>>,
          ReactElement<HTMLAttributes<HTMLTableCellElement>>,
        ];
      }
    >
  >,
];

const StyledTableHead = styled(TableHead)`
  box-shadow: inset 0 -0.0625rem 0 0px rgb(0, 0, 0);
`;

/**
 * The ContentWithStructuredText component has some custom styles which also
 * need to be implemented for the ContentWithHtml component in order for
 * the content to have a consistent appearance between different
 * types of Legal documents.
 *
 * Ticket: https://polestarjira.atlassian.net/browse/PCOM-13017
 */
export const overrideElementMap: HTMLElementMap = {
  h1({ children }) {
    return (
      <ContentHeading level={1} spacing={Spacings.large}>
        {children}
      </ContentHeading>
    );
  },
  h2({ children }) {
    return (
      <ContentHeading
        level={2}
        spacing={Spacings.large}
        variant={ParagraphVariantSizes.medium}
      >
        {children}
      </ContentHeading>
    );
  },
  h3({ children }) {
    return (
      <ContentHeading
        level={3}
        spacing={Spacings.large}
        variant={ParagraphVariantSizes.medium}
      >
        {children}
      </ContentHeading>
    );
  },
  h4({ children }) {
    return (
      <ContentHeading level={4} spacing={Spacings.large}>
        {children}
      </ContentHeading>
    );
  },
  h5({ children }) {
    return (
      <ContentHeading level={5} spacing={Spacings.large}>
        {children}
      </ContentHeading>
    );
  },
  h6({ children }) {
    return (
      <ContentHeading level={6} spacing={Spacings.large}>
        {children}
      </ContentHeading>
    );
  },
  p({
    children,
    isListItem,
  }: Parameters<NonNullable<HTMLElementMap["p"]>>[0] & {
    isListItem?: boolean;
  }) {
    return (
      <ContentParagraph spacing={isListItem ? Spacings.none : Spacings.large}>
        {children}
      </ContentParagraph>
    );
  },
  ul({ children }) {
    return <ContentUnorderedList>{children}</ContentUnorderedList>;
  },
  ol({ children }) {
    return (
      <ContentOrderedList spacing={Spacings.large}>
        {children}
      </ContentOrderedList>
    );
  },
  table({ children }) {
    return (
      <>
        <table>{children}</table>
        <Spacer spacing={Spacings.large} />
      </>
    );
  },
  li({ children }) {
    // Allow child elements to know if their parent element is a list item
    const newChildren = React.Children.map(children, (child) => {
      const newProps = {
        ...(child.props as {
          isFirst?: boolean;
          children?: ReactElement;
        }),
        isListItem: true,
      };

      if (child.type) {
        return React.createElement(
          child.type,
          {
            ...child.props,
            ...newProps,
          },
          newProps.children
        );
      }

      return child;
    });

    return <>{newChildren}</>;
  },
  tbody({ children: tableRows }) {
    if (Array.isArray(tableRows)) {
      const [headerRow, ...tableBodyRows] = tableRows as TableBodyWithHeader;

      // The first row has one column which means it is the table header row
      if (
        headerRow.props.children.length === 1 &&
        tableBodyRows.every((row) => {
          return row.props.children.length === 2;
        })
      ) {
        const styledBodyRows = tableBodyRows.map((row) => {
          const {
            children: [labelElement, valueElement],
          } = row.props;

          const newLabelElementProps = {
            ...labelElement.props,
            children: <Caption>{labelElement.props.children}</Caption>,
          };

          const newRow = {
            ...row,
            props: {
              ...row.props,
              children: [
                { ...labelElement, props: newLabelElementProps },
                valueElement,
              ],
            },
          };

          return newRow;
        });

        return (
          <>
            <StyledTableHead>
              <TableRow>{headerRow.props.children}</TableRow>
            </StyledTableHead>
            <TableBody>{styledBodyRows}</TableBody>
          </>
        );
      }
    }

    return <TableBody>{tableRows}</TableBody>;
  },
};
